// Copyright 2017 Sorama B.V.

import {
    autoinject,
    bindable,
    BindingEngine,
    computedFrom,
    Disposable,
    DOM,
    observable,
    TemplatingEngine,
    transient,
} from "aurelia-framework";
import { GenericMeasurement, MeasurementPartType } from "datatypes/SoramaWebApi";
import { AnalysisModalReturnState, AnalysisModalState } from "../../../modals/Analysis/AnalysisModal";
import { MeasurementInfoModalState } from "../../../modals/MeasurementInfo/MeasurementInfoModal";

import { StatusItem } from "datatypes/Status";
import { MeasurementField, MeasurementRepository } from "repositories/MeasurementRepository";

import { IWorkflowModuleCatalog } from "catalogs/IWorkflowModuleCatalog";
import { WorkflowModalCatalog } from "catalogs/WorkflowModalCatalog";
import { MeasurementWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/MeasurementModuleCatalog";
import { MeasurementFamilyNode } from "datatypes/MeasurementFamilyNode";
import { OrderMode, OrderModes } from "datatypes/OrderModes";
import { MeasurementExtensions } from "extensions/MeasurementExtensions";
import {
    IWorkflowModuleActivate,
    IWorkflowModuleDeactivate,
    IWorkflowModuleInstance,
    IWorkflowModuleModalInstance,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { WorkflowModuleType, WorkflowModuleTypes } from "libraries/WorkflowFramework/Interfaces/IWorkflowModuleConfig";
import {
    WorkflowPropertyBindingMode,
    WorkflowPropertyScope,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { ScopedWorkflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/ModuleSetScopeObserver";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";

import {
    AureliaGridInstance,
    Column,
    FieldType,
    FilterCallbackArg,
    Formatter,
    GridOption,
    OnEventArgs,
} from "aurelia-slickgrid";
import { BindingSignaler } from "aurelia-templating-resources";
import {
    AnalysisWorkflowModuleCatalog,
    IAnalysisWorkflowModuleConfig,
} from "catalogs/WorkflowModuleCatalog/AnalysisWorkflowModuleCatalog";
import { MeasurementProcessorTypes } from "datatypes/MeasurementProcessorTypes";
import { AnalysisPresetModalReturnState } from "modules/modals/AnalysisPreset/AnalysisPresetModal";
import { MeasurementSearchModalState, SearchFilters } from "modules/modals/MeasurementSearch/MeasurementSearch";
import { ParserCatalog } from "parsers/ParserCatalog";
import { ClientStatus, IClientService } from "services/Client/IClientService";
import { MeasurementColorService } from "services/Color/MeasurementColorService";
import { ExportService } from "services/Export/ExportService";
import { IMeasurementProcessorResolver } from "services/Measurement/IMeasurementProcessorResolver";
import { IMeasurementStorageService } from "services/Measurement/IMeasurementStorageService";
import { IProjectManagementService } from "services/Project/IProjectManagementService";
import { IBeamformingProcessingService } from "services/SoundProcessing/IBeamformingProcessingService";
import { ISpectralProcessingService } from "services/SoundProcessing/ISpectralProcessingService";
import { IStorageService, ITypedStorageContainerSet, StorageTypes } from "services/Storage/IStorageService";
import { IUserSettingsService } from "services/UserManagement/IUserSettingsService";
import { WorkflowModeService } from "services/WorkflowModes/WorkflowModeService";
import { ShellViewManager } from "../../../shell/App/ShellViewManager";
import { SpectrumModule } from "../Spectrum/SpectrumModule";

interface OpenAnalysisModule {
    name: string;
    icon: string;
    index: number;
}

interface Selection {
    rowId: string;
    columnId: number;
    selected: boolean;
}

/**
 * How To: add an analysis module
 * 1) Add module in 'moduleIcons'
 * 2) import new module
 * 3) Subscribe to new module in the eventAggregator in the bind function
 */
@transient()
@autoinject
export class AnalysisOverviewModule implements IWorkflowModuleActivate, IWorkflowModuleDeactivate {
    private static InstanceCount = 0;
    public readonly instanceCount: number;

    //#region Static stuff
    private orderModes: OrderMode[] = OrderModes.All;
    private get gridSignalName(): string {
        return "eval-analysis-types" + this.instanceCount;
    }

    //#endregion

    //#region Cleanup stuff

    private recordingGroupSet: ITypedStorageContainerSet;

    private disposables: Disposable[];
    private measurementPromise: Promise<void>;

    //#endregion

    //#region UI properties

    private busyBinding = false;
    private busyLoadingMeasurements = false;
    private moduleInstance: IWorkflowModuleInstance;
    private busyRefreshingMeasurements = false;

    // Slickgrid variables
    protected columnDefinitions: Column[];
    protected gridOptions: GridOption;
    private isReopeningModule = false;

    @observable
    protected filterText: string;

    @observable
    protected filterSelected = false;

    protected searchFilters: SearchFilters;
    private advancedSearchModal: IWorkflowModuleModalInstance<MeasurementSearchModalState, MeasurementSearchModalState>;

    @computedFrom("busyBinding", "busyLoadingMeasurements", "busyRefreshingMeasurements")
    public get busy(): boolean {
        return this.busyBinding || this.busyLoadingMeasurements || this.busyRefreshingMeasurements;
    }

    @computedFrom("busyRefreshingMeasurements")
    public get busyRefreshing(): boolean {
        return this.busyRefreshingMeasurements;
    }

    @observable
    private orderMode: OrderMode = this.orderModes[0];

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay, scope: WorkflowPropertyScope.workflow })
    private selectedMeasurementIdForReport: string;

    @workflowProperty({
        bindingMode: WorkflowPropertyBindingMode.twoWay,
        scope: WorkflowPropertyScope.workflowModuleSet,
        objectType: "string",
    })
    public selectedMeasurementIds: ScopedWorkflowProperty<string[]>;

    filterButtonStatus = false;

    private availableMeasurements: MeasurementFamilyNode[] = [];
    private notAvailableMeasurements: MeasurementFamilyNode[] = [];

    private openModules: OpenAnalysisModule[];

    private measurementAvailableStatus: { [analysisType: string]: { [id: string]: StatusItem<boolean> } } = {};

    @bindable public recordingId = "";
    @bindable public modalOpen: boolean;

    public get anyAvailableMeasurements(): boolean {
        return this.availableMeasurements && this.availableMeasurements.length > 0;
    }

    public get selectedProjectHasNoMeasurement(): boolean {
        return (
            this.projectManagementService.currentProject.item &&
            (!this.projectManagementService.measurements.item || this.analyzableMeasurements.length == 0)
        );
    }

    private get analyzableMeasurements(): GenericMeasurement[] {
        return this.projectManagementService.measurements.item.filter((x) => x.parts.length > 0);
    }

    public get selectedModeCanDoRealTimeRecording(): boolean {
        return this.workflowModeService.selectedWorkflowMode.canDoRealTimeRecording;
    }

    @computedFrom("availableMeasurements", "availableMeasurements.length")
    private get allMeasurements(): GenericMeasurement[] {
        if (!this.availableMeasurements) return [];

        const measurements = this.availableMeasurements.map((x) => x.all);

        return (<GenericMeasurement[]>[]).concat(...measurements);
    }

    //#endregion

    //#region Constructors and lifecycle

    constructor(
        private readonly bindingEngine: BindingEngine,
        private readonly projectManagementService: IProjectManagementService,
        private readonly workflowModeService: WorkflowModeService,
        private readonly storageService: IStorageService,
        private readonly clientService: IClientService,
        private readonly processorResolver: IMeasurementProcessorResolver,
        private readonly measurementColorService: MeasurementColorService,
        private readonly measurementRepository: MeasurementRepository,
        private readonly measurementStorageService: IMeasurementStorageService,
        private readonly moduleCatalog: IWorkflowModuleCatalog,
        private readonly shellViewManager: ShellViewManager,
        private readonly templatingEngine: TemplatingEngine,
        private readonly bindingSignaller: BindingSignaler,
        private readonly exportService: ExportService,
        private readonly userSettingsService: IUserSettingsService
    ) {
        this.instanceCount = AnalysisOverviewModule.InstanceCount;
        AnalysisOverviewModule.InstanceCount++;

        this.generateTableDefinition();
    }

    public activateModule(moduleInstance: IWorkflowModuleInstance): void {
        this.moduleInstance = moduleInstance;
        this.busyBinding = true;

        // Set static stuff
        this.disposables = [
            this.moduleInstance.getWorkflow().subscribeModulesChanged(() => {
                // Detects whether something has changed in the modal instance
                this.selectedMeasurementIdsChanged();
            }),
            this.bindingEngine
                .propertyObserver(this.projectManagementService, "measurements")
                .subscribe(this.measurementsChanged),
            this.bindingEngine.propertyObserver(this.clientService, "status").subscribe(this.clientStatusChanged),
            this.bindingEngine
                .propertyObserver(this.clientService, "storagePath")
                .subscribe(this.clientStoragePathChanged),
            this.bindingEngine.propertyObserver(this.moduleInstance, "expanded").subscribe(() => {
                this.aureliaGridObj.resizerService.resizeGrid();
            }),
            this.processorResolver.registerProcessorStatusListener(() => this.measurementsChanged()),
            this.measurementRepository.measurementUpdateObserver(this.measurementUpdated),
        ];
        // Add resize observer
        // Defer grid resize to the next frame to avoid ResizeObserver feedback loops
        let resizeRaf: number | undefined;
        const resizeObserver = new ResizeObserver(() => {
            if (!this.aureliaGridObj) {
                return;
            }

            if (resizeRaf) {
                cancelAnimationFrame(resizeRaf);
            }

            resizeRaf = requestAnimationFrame(() => {
                resizeRaf = undefined;
                this.aureliaGridObj?.resizerService.resizeGrid();
            });
        });
        resizeObserver.observe(document.getElementById("analysis-overview-module"));
        this.disposables.push({
            dispose: () => {
                if (resizeRaf) {
                    cancelAnimationFrame(resizeRaf);
                }

                resizeObserver.disconnect();
            },
        });

        // Make sure that the selectedMeasurementsIds correspond with the current UI state
        const moduleIds = this.moduleInstance
            .getWorkflow()
            .getModules()
            .filter((x) => WorkflowModuleTypes.AllAnalysis.find((y) => y == x.config.moduleType))
            .map((x) => x.moduleId);

        for (const moduleId of moduleIds) {
            if (!this.selectedMeasurementIds.has(moduleId)) {
                this.selectedMeasurementIds.add(moduleId, []);
            }
        }

        // If no modules are open, add the analysis module
        if (moduleIds.length == 0) {
            this.openAnalysisModal();
        }

        this.moduleInstance.dispatch(async () => {
            try {
                await this.projectManagementService.refreshMeasurements();
                this.storageService
                    .getTypedStorageContainerSet(StorageTypes.recordingGroup)
                    .catch(() => undefined)
                    .then((groupSet) => {
                        if (groupSet) {
                            this.disposables.push(groupSet);
                            this.recordingGroupSet = groupSet;
                        }

                        this.openModules = [];

                        // Trigger change handlers here
                        this.measurementsChanged();
                    });
            } catch (error) {
                console.error("Error activating the module:", error);
            } finally {
                this.busyBinding = false;
            }
        });
    }

    public deactivateModule(): void {
        // Cleanup here
        while (this.disposables.length > 0) {
            this.disposables.pop().dispose();
        }

        this.aureliaGridObj?.dispose();
    }

    //#endregion

    //#region UI Callbacks

    public addModule = (recordingId?: string): void => {
        let measurement: GenericMeasurement;

        if (recordingId) {
            this.recordingId = recordingId;
            this.modalOpen = true;

            const measurementFamily = this.availableMeasurements.find((x) => x.some((y) => y.id === recordingId));
            if (measurementFamily) {
                if (measurementFamily.measurement.id === recordingId) {
                    measurement = measurementFamily.measurement;
                } else {
                    measurement = measurementFamily.find((x) => x.id === recordingId);
                }
            }
        }

        if (this.workflowModeService.selectedWorkflowMode.isIndustrial) {
            this.openAnalysisPresetModal(measurement);
        } else {
            this.openAnalysisModal(measurement);
        }
    };

    private openAnalysisModal(measurement?: GenericMeasurement): void {
        this.moduleInstance
            .createModal<AnalysisModalState, AnalysisModalReturnState>(WorkflowModalCatalog.AnalysisModal)
            .withState({
                measurement: measurement,
                selectedMeasurementIds: this.selectedMeasurementIds,
                recordingId: this.recordingId,
            })
            .show();
    }

    private openAnalysisPresetModal(measurement: GenericMeasurement): void {
        this.moduleInstance
            .createModal<AnalysisModalState, AnalysisPresetModalReturnState>(WorkflowModalCatalog.AnalysisPresetModal)
            .withState({
                measurement: measurement,
                selectedMeasurementIds: this.selectedMeasurementIds,
                recordingId: this.recordingId,
            })
            .whenClosed((x) => {
                let promise: Promise<unknown> = Promise.resolve();

                if (x.selectedAnalysisModulePresets) {
                    promise = Promise.all(
                        x.selectedAnalysisModulePresets.map((p) => {
                            p.defaultSettings.forEach((setting) => {
                                const parser = setting.valueType && ParserCatalog.getParser(setting.valueType);
                                const settingValue = parser ? parser.toWorkflowProperty(setting.value) : setting.value;
                                this.userSettingsService.set(p.settingsName, setting.name, settingValue as string);
                            });

                            return this.moduleInstance
                                .getWorkflow()
                                .addModule(p.moduleConfig)
                                .then((newModule) => {
                                    this.selectedMeasurementIds.add(newModule.moduleId, [this.recordingId]);
                                    newModule.moduleInstance.setWorkflowProperties(p.defaultProperties);

                                    if (newModule.moduleInstance.moduleReference.moduleName == "spectrumanalysis") {
                                        (
                                            newModule.moduleInstance.moduleReference as unknown as SpectrumModule
                                        )?.initializeAnalysisPreset(x.analysisType);
                                    }
                                });
                        })
                    );
                }

                return promise.then(() => {
                    this.recordingId = "";
                    this.modalOpen = false;
                });
            })
            .show();
    }

    public refreshMeasurements(): void {
        if (!this.busyRefreshingMeasurements) {
            this.busyRefreshingMeasurements = true;
            this.projectManagementService.refreshMeasurements().then(() => {
                this.busyRefreshingMeasurements = false;
            });
        }
    }

    // #endregion

    //#region Change Handlers

    private selectedMeasurementIdsChanged(): void {
        if (this.selectedMeasurementIds.observedModuleIds.length == 0) {
            this.filterButtonStatus = false;
        } else {
            const mod = this.selectedMeasurementIds.observedModuleIds.find((index) =>
                Array.isArray(this.selectedMeasurementIds[index])
            );
            if (mod && this.selectedMeasurementIds[mod].length !== 0) {
                this.filterButtonStatus = true;
            } else {
                this.filterButtonStatus = false;
            }
        }
        this.syncAnalysisColumns();
    }

    private measurementUpdated = (property: MeasurementField, measurement: GenericMeasurement) => {
        // Update measurement fields
        if (this.availableMeasurements) {
            for (const fam of this.availableMeasurements) {
                if (
                    MeasurementExtensions.updateMeasurementField(
                        fam.measurement,
                        measurement.id,
                        property,
                        measurement[property]
                    )
                ) {
                    return;
                }

                // Also check the children
                for (const child of fam.children) {
                    if (
                        MeasurementExtensions.updateMeasurementField(
                            child.measurement,
                            measurement.id,
                            property,
                            measurement[property]
                        )
                    ) {
                        return;
                    }
                }
            }
        }

        if (this.notAvailableMeasurements) {
            for (const fam of this.notAvailableMeasurements) {
                if (
                    MeasurementExtensions.updateMeasurementField(
                        fam.measurement,
                        measurement.id,
                        property,
                        measurement[property]
                    )
                ) {
                    return;
                }

                // Also check the children
                for (const child of fam.children) {
                    if (
                        MeasurementExtensions.updateMeasurementField(
                            child.measurement,
                            measurement.id,
                            property,
                            measurement[property]
                        )
                    ) {
                        return;
                    }
                }
            }
        }

        this.syncAnalysisColumns();
    };

    private measurementsChanged = () => {
        // Clear measurement status
        this.measurementAvailableStatus = {};

        if (!this.projectManagementService.currentProject.item) return;

        // Determine which measurements are available for analysis
        if (!this.projectManagementService.measurements.status.available) return;

        this.busyLoadingMeasurements = true;

        const promise = this.measurementPromise ? this.measurementPromise : Promise.resolve();

        this.measurementPromise = promise.then(() => {
            // Make sure all the measurements have a color assigned
            for (let i = 0; i < this.analyzableMeasurements.length; i++) {
                const measurement = this.analyzableMeasurements[i];

                this.measurementColorService.ensureAssignedColor({ measurementId: measurement.id });
            }

            // Check if there are any selected measurement ids that are not available anymore
            for (const index of this.selectedMeasurementIds) {
                const ids = index;

                if (ids == undefined) {
                    continue;
                }

                for (let i = 0; i < ids.length; i++) {
                    const id = ids[i];

                    // Use the measurement repository to check
                    if (!this.measurementRepository.measurementIds.some((x) => x === id)) {
                        // Remove the id
                        ids.splice(i, 1);
                    }
                }
            }

            return this.processorResolver
                .canProcess(this.analyzableMeasurements)
                .then((measurementAvailability) => {
                    const availableOrComputed = measurementAvailability
                        .filter((x) => x.available || x.measurement.isComputedMeasurement)
                        .map((x) => x.measurement);
                    const notAvailableOrComputed = measurementAvailability
                        .filter((x) => !x.available || x.measurement.isComputedMeasurement)
                        .map((x) => x.measurement);

                    this.availableMeasurements = MeasurementFamilyNode.fromSet(availableOrComputed);
                    this.notAvailableMeasurements = MeasurementFamilyNode.fromSet(notAvailableOrComputed);

                    this.syncAnalysisColumns();
                })
                .finally(() => {
                    this.busyLoadingMeasurements = false;
                });
        });
    };

    private clientStatusChanged = (newStatus: ClientStatus, oldStatus: ClientStatus) => {
        const newAvailable = newStatus === ClientStatus.Available || newStatus === ClientStatus.OutdatedVersion;
        const oldAvailable = oldStatus === ClientStatus.Available || oldStatus === ClientStatus.OutdatedVersion;

        if (newAvailable !== oldAvailable) {
            // Update measurement status
            this.measurementsChanged();
        }
    };

    private clientStoragePathChanged = (newValue: string, oldValue: string): void => {
        if (newValue === oldValue || oldValue === undefined) return;

        // Update measurement status
        this.measurementsChanged();
    };

    private orderModeChanged(): void {
        const filterableName = (measurement: GenericMeasurement) => {
            let name = measurement.name + measurement.id;

            let currentItem = measurement;
            while (currentItem.isComputedMeasurement) {
                currentItem = this.allMeasurements.find((x) => x.id == currentItem.computedMeasurementSourceId);
                if (!currentItem) break;
                name = currentItem.name + currentItem.id + name;
            }

            return name;
        };

        const orderMode = this.orderMode;

        const comparer = function (a: GenericMeasurement, b: GenericMeasurement) {
            if (a.computedMeasurementSourceId == b.id) {
                return 1;
            }
            if (b.computedMeasurementSourceId == a.id) {
                return -1;
            }

            let aTotalName = filterableName(a);
            let bTotalName = filterableName(b);

            let sign = 1;
            switch (orderMode) {
                case OrderModes.NameAscending:
                    break;
                case OrderModes.NameDescending:
                    sign = -1;
                    break;
                case OrderModes.MeasuredAscending:
                    aTotalName = a.start + aTotalName;
                    bTotalName = b.start + bTotalName;
                    break;
                case OrderModes.MeasuredDescending:
                    aTotalName = a.start + aTotalName;
                    bTotalName = b.start + bTotalName;
                    sign = -1;
                    break;
                case OrderModes.DurationAscending:
                    aTotalName = a.duration + aTotalName;
                    bTotalName = b.duration + bTotalName;
                    break;
                case OrderModes.DurationDescending:
                    aTotalName = a.duration + aTotalName;
                    bTotalName = b.duration + bTotalName;
                    sign = -1;
                    break;
            }

            return aTotalName < bTotalName ? -sign : sign;
        };

        if (this.aureliaGridObj) {
            this.aureliaGridObj.dataView.sort(comparer);
        }
    }

    //#endregion

    // #region Update Functions

    private aureliaGridObj: AureliaGridInstance;

    private aureliaGridReady(aureliaGrid: AureliaGridInstance) {
        this.aureliaGridObj = aureliaGrid;

        this.aureliaGridObj.slickGrid.onHeaderClick.subscribe((e, args) => {
            const column: Column = args.column;

            if (column.formatter == this.isAnalysisTypeSelectedFormatter) {
                const mod = this.moduleInstance.getWorkflow().getModule(Number(column.id));
                if (mod) {
                    mod.makeActive();
                    e.stopPropagation();
                }
            }
        });

        this.orderModeChanged();
    }

    private filterSelectedChanged() {
        if (this.aureliaGridObj) {
            this.filterTextChanged();
            this.aureliaGridObj.slickGrid.invalidate();
        }
    }

    private filterTextChanged(): void {
        const filterColumn = this.columnDefinitions[0];
        const filter = {};
        const filterArg: FilterCallbackArg = {
            columnDef: filterColumn,
            searchTerms: [this.filterText || ""],
        };

        if (this.filterText) {
            filter[filterColumn.field] = filterArg;
        }

        this.aureliaGridObj.dataView.setFilterArgs({
            columnFilters: filter,
            grid: this.aureliaGridObj.slickGrid,
        });

        this.aureliaGridObj.dataView.setFilter(this.measurementsFilter);
    }

    private syncAnalysisColumns() {
        if (!this.aureliaGridObj || this.isReopeningModule) {
            // Grid is still initializing or we are reopening a module inplace
            return;
        }

        let columnsChanged = false;
        const grid = this.aureliaGridObj.slickGrid;
        const columns: Column[] = grid.getColumns();

        for (const index of this.selectedMeasurementIds.observedModuleIds) {
            // Note: indices should be unique in a lifetime of the portal
            const currentColumn = columns.find((x) => x.id == index);

            if (
                typeof this.moduleInstance === "undefined" ||
                typeof this.moduleInstance.getWorkflow() === "undefined"
            ) {
                continue;
            }

            const mod = this.moduleInstance.getWorkflow().getModule(index);

            if (!currentColumn && mod) {
                columnsChanged = true;

                // Non existent, so add a new one
                const newColumn: Column = {
                    id: index,
                    field: "measurement",
                    name: `<i class="${mod.config.icon}">`,
                    minWidth: 30,
                    maxWidth: 30,
                    formatter: this.isAnalysisTypeSelectedFormatter,
                    asyncPostRender: this.postRenderIsAnalysisAvailable,
                    params: mod,
                    resizable: false,
                };

                columns.splice(columns.length, 0, newColumn);
            }
        }

        // Remove (analysis) columns without any selected items and without any visible workflow modules
        const newColumns = columns.filter(
            (x) =>
                x.formatter != this.isAnalysisTypeSelectedFormatter ||
                (this.selectedMeasurementIds.has(Number(x.id)) &&
                    !!this.moduleInstance.getWorkflow().getModule(Number(x.id)))
        );

        if (columnsChanged || newColumns.length != columns.length) {
            grid.setColumns(newColumns);
            this.aureliaGridObj.resizerService.resizeGrid();
        } else {
            this.bindingSignaller.signal(this.gridSignalName);
        }
    }

    private isAnalysisTypeSelectedFormatter: Formatter = (
        row,
        cell,
        measurement,
        columnDef,
        dataContext: GenericMeasurement
    ) => {
        return "";
    };

    private measurementsFilter = (item: GenericMeasurement, args) => {
        // Check advanced search, before checking the name via the search bar
        if (this.searchFilters) {
            const advancedFilter = this.advancedFilter(item, this.searchFilters);
            if (!advancedFilter) {
                return false;
            }
        }

        const filterText = this.filterText ? this.filterText : "";
        const openModules = this.moduleInstance.getWorkflow().getModules();

        const filteredVisible = (measurement: GenericMeasurement) => {
            for (const id of this.selectedMeasurementIds.observedModuleIds) {
                if (
                    this.selectedMeasurementIds.get(id).find((x) => x == measurement.id) &&
                    openModules.find((x) => x.moduleId == id)
                ) {
                    return true;
                }
            }

            return false;
        };

        // Find the group we belong to
        const node = this.availableMeasurements.find((x) => x.some((y) => y.id == item.id));
        return node.some(
            (x) =>
                x.name.toLowerCase().indexOf(filterText.toLowerCase()) >= 0 &&
                (!this.filterSelected || filteredVisible(x))
        );
    };

    private addModuleClick = (e: KeyboardEvent | MouseEvent, args: OnEventArgs) => {
        const value: MeasurementFamilyNode = args.dataContext;
        this.addModule(value.id);
    };

    private async onAnalysisClick(moduleId: number, value: GenericMeasurement) {
        const selectedMeasurementIds = this.selectedMeasurementIds.get(moduleId);
        const idx = selectedMeasurementIds.findIndex((x) => x == value.id);

        if (idx >= 0) {
            // Measurement is already selected, so deselect it
            selectedMeasurementIds.splice(idx, 1);
        } else {
            const mod = this.moduleInstance.getWorkflow().getModule(moduleId);
            // Check if the analysis type supports analyzing multiple measurements. Otherwise, deselect others
            if (mod && mod.config && (<IAnalysisWorkflowModuleConfig>mod.config).singleMeasurementAnalysis) {
                // Setting this to true will prevent re ordering of the columns in the slick grid
                this.isReopeningModule = true;

                const ids = this.selectedMeasurementIds.get(moduleId);
                ids.splice(0, ids.length);

                // Reopen the module with the new measurement
                // First, close the old module
                const modulePos = this.moduleInstance.getWorkflow().getModulePosition(moduleId);
                await mod.close();

                // Add the new module at the same position
                await this.moduleInstance
                    .getWorkflow()
                    .addModule(mod.config, modulePos)
                    .then((newModule) => {
                        this.selectedMeasurementIds.add(newModule.moduleId, [value.id]);
                    });
            }
            selectedMeasurementIds.push(value.id);
        }

        this.bindingSignaller.signal(this.gridSignalName);
        this.isReopeningModule = false;
    }

    private isMeasurementSelected(moduleId: number, measurementId: string): boolean {
        if (!this.selectedMeasurementIds.has(moduleId)) {
            return false;
        }

        const mod = this.selectedMeasurementIds[moduleId];

        if (mod && Array.isArray(mod)) {
            return mod.findIndex((x) => x == measurementId) >= 0;
        }

        return false;
    }

    private postRenderIsAnalysisAvailable = (
        domCellNode: unknown,
        row: number,
        dataContext: GenericMeasurement,
        columnDef: Column
    ) => {
        const domNod: HTMLDivElement = domCellNode[0];

        const mod: IAnalysisWorkflowModuleConfig = columnDef.params.config;
        this.isAnalysisAvailableForType(dataContext, mod.moduleType).then((isAvailable) => {
            const wrapperEl = DOM.createElement("div");

            if (isAvailable) {
                wrapperEl.innerHTML = `<a if.bind="isMeasurementSelected(${columnDef.id},'${dataContext.id}') & signal:'${this.gridSignalName}'\">
                    <i class="fas fa-check"></i></a><a else><i class="fas fa-check-circle disabled"></i></a>`;

                columnDef.onCellClick = (e: KeyboardEvent | MouseEvent, args: OnEventArgs) => {
                    this.onAnalysisClick(Number(args.columnDef.id), args.dataContext);
                };
            } else {
                // eslint-disable-next-line quotes
                wrapperEl.innerHTML = '<i class="fas fa-minus"></i>';
            }

            // A blog explaining this feature: https://ilikekillnerds.com/2016/01/enhancing-at-will-using-aurelias-templating-engine-enhance-api/
            this.templatingEngine.enhance({ element: wrapperEl, bindingContext: this });

            domNod.insertBefore(wrapperEl, domNod.firstChild);
        });
    };

    private postRenderMeasurementName = (
        domCellNode: unknown,
        row: number,
        dataContext: GenericMeasurement,
        columnDef: Column
    ) => {
        let nrOfParents = 0;
        let currentMeasurement = dataContext;

        while (currentMeasurement && currentMeasurement.isComputedMeasurement) {
            nrOfParents++;
            currentMeasurement = this.allMeasurements.find(
                (x) => x.id == currentMeasurement.computedMeasurementSourceId
            );
        }

        // For future reference. It is only possible to enhance an element with a custom control,
        // if the custom control is a global resource. This can be added in aurelia install pipeline in the main.ts
        const domNod: HTMLDivElement = domCellNode[0];
        domNod.style.display = "inline-flex";

        // Create wrapper
        const wrapperEl = DOM.createElement("div");
        const leftPaddingEm = nrOfParents * 1.3;
        wrapperEl.setAttribute("style", `display: inline-flex; padding-left: ${leftPaddingEm}em;`);

        // Append color picker
        const colorPickerDiv = DOM.createElement("div");
        colorPickerDiv.style.display = "inline";
        colorPickerDiv.style.width = "1.5em";
        const childEl = DOM.createElement("measurement-color-picker");

        childEl.setAttribute("measurement.bind", "$this");
        colorPickerDiv.appendChild(childEl);
        wrapperEl.appendChild(colorPickerDiv);

        // Append storage and measurement type
        const storageEl = DOM.createElement("i");

        if (!dataContext.isComputedMeasurement) {
            storageEl.innerHTML = this.measurementStorageService.measurementStorageInfos[dataContext.id].dataOnline
                ? // eslint-disable-next-line quotes
                  '<i class="fas fa-cloud-upload"></i>'
                : // eslint-disable-next-line quotes
                  '<i class="fas fa-hdd"></i>';
        } else {
            storageEl.innerHTML =
                dataContext.computedMeasurementProperties.ProcessorType == MeasurementProcessorTypes.beamformProbe
                    ? // eslint-disable-next-line quotes
                      '<i class="fas fa-map-pin"></i>'
                    : // eslint-disable-next-line quotes
                      '<i class="fas fa-hourglass-half"></i>';
        }
        storageEl.style.display = "inline";
        storageEl.style.paddingRight = "8px";
        wrapperEl.appendChild(storageEl);

        // Display on the right side of the field
        const behindEl = DOM.createElement("div");
        behindEl.style.display = "inline";
        behindEl.style.float = "right";

        // Append info, muted mics
        const infoElement = DOM.createElement("a");
        const prefix =
            currentMeasurement.mutedSensors && currentMeasurement.mutedSensors.length > 0
                ? // eslint-disable-next-line quotes
                  '<i class="fas fa-asterisk fa-xs" title="Microphones in this measurement have been muted"></i>'
                : "";
        infoElement.innerHTML =
            prefix +
            // eslint-disable-next-line quotes
            `<i class="fas fa-info-circle" title="Measurement Info"></i>`;
        infoElement.onclick = (event) => {
            event.stopPropagation(); // Prevent clicking button below
            this.showInfo(dataContext);
        };
        infoElement.style.paddingRight = "5px";
        behindEl.appendChild(infoElement);

        // Append Report
        const reportElement = DOM.createElement("a");
        reportElement.innerHTML =
            // eslint-disable-next-line quotes
            '<i class="fas fa-file" title="Create a Report"></i>';
        reportElement.onclick = (event) => {
            event.stopPropagation(); // Prevent clicking button below
            this.showReport(dataContext);
        };
        reportElement.classList.add("analysis-overview-link-icon");
        behindEl.appendChild(reportElement);

        // Enhance Aurelia templating
        this.templatingEngine.enhance({ element: wrapperEl, bindingContext: dataContext });

        // Get first text element and wrap in span
        const textNode = domNod.firstChild;
        const spanEl = document.createElement("span");
        spanEl.appendChild(textNode);
        spanEl.style.overflow = "hidden";
        spanEl.style.textOverflow = "ellipsis";
        spanEl.style.flexGrow = "1";
        domNod.insertBefore(spanEl, domNod.firstChild);

        // Insert wrapper and behind elements
        domNod.insertBefore(wrapperEl, domNod.firstChild);
        domNod.insertAdjacentElement("beforeend", behindEl);
    };

    private generateTableDefinition(): void {
        this.columnDefinitions = [
            {
                id: "name",
                name: "Name",
                field: "name",
                minWidth: 150,
                type: FieldType.string,
                onCellClick: this.addModuleClick,
                asyncPostRender: this.postRenderMeasurementName,
                excludeFromColumnPicker: true,
            },
        ];

        this.gridOptions = {
            enableAutoResize: true,
            autoResize: {
                containerId: "slickgrid-container-analyisoverview",
                calculateAvailableSizeBy: "container",
            },
            enableAsyncPostRender: true,
            enableColumnReorder: false,
            enableRowSelection: true,
            enableCellNavigation: true,
            enableGridMenu: false,
            enableContextMenu: false,
            enableHeaderMenu: false,
            enableColumnPicker: false,
            enableAutoSizeColumns: true,
            autoFitColumnsOnFirstLoad: true,
            enableSorting: true,
            enableFiltering: true,
            showHeaderRow: false,
        };
    }

    //#endregion

    //#region Helper Functions

    private isAnalysisAvailableForType(
        measurement: GenericMeasurement,
        analysisType: WorkflowModuleType
    ): Promise<boolean> {
        if (
            analysisType === WorkflowModuleType.FarFieldAnalysis &&
            ((measurement.isComputedMeasurement &&
                measurement.computedMeasurementProperties.ProcessorType == MeasurementProcessorTypes.beamformProbe) ||
                measurement.parts.filter((x) => x.measurementType == MeasurementPartType.Audio).length > 1)
        )
            return Promise.resolve(false);

        if (
            analysisType === WorkflowModuleType.NearFieldAnalysis &&
            (!measurement.uraProperties ||
                measurement.uraProperties.sensorHeight < 8 ||
                measurement.uraProperties.sensorWidth < 8)
        )
            return Promise.resolve(false);

        let promise: Promise<{ measurement: GenericMeasurement; available: boolean }[]> = Promise.resolve([
            { measurement: measurement, available: false },
        ]);
        // Also check if the processor resolver allows processing of this measurement
        switch (analysisType) {
            case WorkflowModuleType.FarFieldAnalysis:
                promise = this.processorResolver.canProcess([measurement], IBeamformingProcessingService);
                break;
            case WorkflowModuleType.NearFieldAnalysis:
                // Holography is always available if the data is available
                promise = Promise.resolve([{ measurement: measurement, available: true }]);
                break;
            case WorkflowModuleType.GenericAnalysis:
                promise = this.processorResolver.canProcess([measurement], ISpectralProcessingService);
                break;
            case WorkflowModuleType.PsychoAcousticsAnalysis:
                promise = Promise.resolve([
                    {
                        measurement: measurement,
                        available: MeasurementExtensions.getMicrophonePositionsForMeasurement(measurement).length > 0,
                    },
                ]);
                break;
        }

        return promise.then((x) => x[0].available);
    }

    public editProject(): void {
        this.shellViewManager.openRightSideBar(MeasurementWorkflowModuleCatalog.ProjectManagementModule);
    }

    public showInfo = (measurement: GenericMeasurement): void => {
        this.moduleInstance
            .createModal<MeasurementInfoModalState>(WorkflowModalCatalog.MeasurementInfo)
            .withState({ measurement: measurement, refreshMeasurements: this.refreshMeasurements.bind(this) })
            .whenCanceled(() => {
                this.refreshMeasurements();
            })
            .show();
    };

    private showReport = (measurement: GenericMeasurement): void => {
        this.selectedMeasurementIdForReport = measurement.id;

        const workflow = this.shellViewManager.activeWorkflow;

        if (workflow != undefined && this.shellViewManager.activeWorkflow.workflowName == "Analysis") {
            this.shellViewManager.openRightSideBar(
                AnalysisWorkflowModuleCatalog.ReportModule,
                workflow.workflowProperties
            );
        }
    };

    public clearFilter(obj: SearchFilters, propertyName: string): void {
        obj[propertyName] = undefined;

        if (this.advancedSearchModal && this.advancedSearchModal.isOpen) {
            const state: MeasurementSearchModalState = {
                measurements: this.allMeasurements,
                filters: this.searchFilters,
                filteredMeasurementCallback: (filter: SearchFilters) => {
                    this.searchFilters = filter;
                    this.filterTextChanged();
                },
            };

            this.advancedSearchModal.updateState(state);
        } else {
            this.filterTextChanged();
        }
    }

    public async openAdvancedSearch(): Promise<void> {
        const state: MeasurementSearchModalState = {
            measurements: this.allMeasurements,
            filters: this.searchFilters,
            filteredMeasurementCallback: (filter: SearchFilters) => {
                this.searchFilters = filter;
                this.filterTextChanged();
            },
        };

        if (this.advancedSearchModal != undefined && this.advancedSearchModal.isOpen) {
            this.advancedSearchModal.updateState(state);
        } else {
            this.advancedSearchModal = await this.moduleInstance
                .createModal<MeasurementSearchModalState>(WorkflowModalCatalog.MeasurementSearch)
                .withState(state)
                .show();
        }
    }

    private advancedFilter(measurement: GenericMeasurement, filter: SearchFilters): boolean {
        const metadataFilters =
            filter.metadataFilters && filter.metadataFilters.length > 0
                ? filter.metadataFilters.filter((x) => x.filter && (x.filter as unknown[]).length > 0)
                : [];

        // Check name
        if (
            filter.nameFilter &&
            filter.nameFilter.length > 0 &&
            measurement.name &&
            !measurement.name.toLowerCase().includes(filter.nameFilter.toLowerCase())
        ) {
            return false;
        }

        // Check notes
        if (
            filter.noteFilter &&
            filter.noteFilter.length > 0 &&
            (!measurement.notes || !measurement.notes.toLowerCase().includes(filter.noteFilter.toLowerCase()))
        ) {
            return false;
        }

        // distance
        if (
            filter.distanceRange &&
            measurement.distance &&
            !(measurement.distance >= filter.distanceRange.from && measurement.distance <= filter.distanceRange.to)
        ) {
            return false;
        }

        // Measurement time
        if (
            filter.measurementTimeRange &&
            measurement.duration &&
            !(
                measurement.duration >= filter.measurementTimeRange.from &&
                measurement.duration <= filter.measurementTimeRange.to
            )
        ) {
            return false;
        }

        // Measurement date
        const createdDate = new Date(measurement.created);
        if (
            filter.measurementDateRange &&
            createdDate &&
            !(createdDate >= filter.measurementDateRange.from && createdDate <= filter.measurementDateRange.to)
        ) {
            return false;
        }

        // Metadata search
        for (const metadataFilter of metadataFilters) {
            if (
                measurement.metadata == undefined ||
                measurement.metadata[metadataFilter.key] == undefined ||
                !measurement.metadata[metadataFilter.key].includes((metadataFilter.filter as string).toLowerCase())
            ) {
                return false;
            }
        }

        return true;
    }

    //#endregion
}
